import { useState } from "react";
import {
  CheckCircle2,
  Clock3,
  LockKeyhole,
  ShieldCheck,
  Trash2,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { HelpTip, PageHeader } from "@/components/common";
import { EmptyState, PageError, PageLoading } from "@/components/LiveState";

// Each role says what it is and, on the second line, what it can do on this page — so the card a person
// recognises as their own also tells them whether they can change who has access.
export const ROLE_DEFINITIONS = [
  {
    value: "owner",
    name: "Owner",
    description: "Full workspace access, including Owners, Admins, and Members.",
    onThisPage: "Adds and removes anyone here.",
  },
  {
    value: "admin",
    name: "Admin",
    description: "Can manage Admins and Members, but cannot grant Owner access.",
    onThisPage: "Adds and removes Admins and Members here.",
  },
  {
    value: "member",
    name: "Member",
    description: "Can use the workspace but cannot manage Team access.",
    onThisPage: "Can see who has access, but not change it.",
  },
];

export const AUTOMATIC_ACCESS_LABEL = "Automatic access";
export const AUTOMATIC_ACCESS_DESCRIPTION =
  "Granted automatically by the workspace's saved company website domain or by the XScale staff policy. This baseline access cannot be removed from the Team page.";

const ROLE_LABELS = Object.fromEntries(ROLE_DEFINITIONS.map((role) => [role.value, role.name]));

export function normalizeEmailDraft(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function roleOptionsForAccess(access) {
  const allowed = Array.isArray(access?.allowedRoles) ? access.allowedRoles : [];
  return ROLE_DEFINITIONS.filter((role) => allowed.includes(role.value));
}

function formatTime(value) {
  if (!value) return "Not reported";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not reported" : date.toLocaleString();
}

function statePresentation(state) {
  if (state === "active") {
    return { label: "Active", icon: CheckCircle2, classes: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (state === "revoked") {
    return { label: "Removed", icon: X, classes: "border-rose-200 bg-rose-50 text-rose-700" };
  }
  return { label: "Pending sign-in", icon: Clock3, classes: "border-amber-200 bg-amber-50 text-amber-700" };
}

function AccessBadge({ state }) {
  const presentation = statePresentation(state);
  const Icon = presentation.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${presentation.classes}`}>
      <Icon size={13} />
      {presentation.label}
    </span>
  );
}

function AddMemberPanel({ access, onCancel, onSubmit, busy }) {
  const roleOptions = roleOptionsForAccess(access);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(roleOptions.at(-1)?.value ?? "member");
  const normalizedEmail = normalizeEmailDraft(email);
  const canSubmit = normalizedEmail.includes("@") && roleOptions.some((option) => option.value === role) && !busy;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    await onSubmit({ email: normalizedEmail, role });
  };

  return (
    <form onSubmit={submit} className="mb-6 rounded-2xl border border-sm-primary/20 bg-sm-primary/[0.035] p-5" data-testid="add-member-panel">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end">
        <label className="min-w-0 flex-1">
          <span className="mb-2 block text-xs font-extrabold uppercase tracking-[0.09em] text-sm-ink/55">Google account email</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.com"
            required
            className="w-full rounded-xl border border-sm-ink/15 bg-white px-4 py-3 text-sm text-sm-ink outline-none transition focus:border-sm-primary/50 focus:ring-4 focus:ring-sm-primary/10"
          />
        </label>
        <label className="w-full lg:w-52">
          <span className="mb-2 block text-xs font-extrabold uppercase tracking-[0.09em] text-sm-ink/55">Workspace role</span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
            className="w-full rounded-xl border border-sm-ink/15 bg-white px-4 py-3 text-sm font-semibold text-sm-ink outline-none transition focus:border-sm-primary/50 focus:ring-4 focus:ring-sm-primary/10"
          >
            {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.name}</option>)}
          </select>
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-xl bg-sm-primary px-5 py-3 text-sm font-bold text-white transition hover:bg-sm-primary/90 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {busy ? "Adding…" : "Add member"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-xl border border-sm-ink/15 bg-white px-4 py-3 text-sm font-bold text-sm-ink/65 transition hover:border-sm-ink/30 disabled:opacity-45"
          >
            Cancel
          </button>
        </div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-sm-ink/55">
        This saves a pending workspace authorization in Supabase. No user is forged and no Auth invitation email is sent. Access activates automatically only after Google verifies this exact email.
      </p>
    </form>
  );
}

function MemberRow({ member, access, busy, readOnly = false, confirmEmail, onRoleChange, onRequestRemove, onConfirmRemove, onCancelRemove }) {
  const roleOptions = roleOptionsForAccess(access);
  const editable = member.canEdit && member.state !== "revoked" && !busy && !readOnly;
  const removable = member.canRemove && member.state !== "revoked" && !busy && !readOnly;
  const confirming = confirmEmail === member.email;

  return (
    <div className={`rounded-2xl border p-5 ${member.state === "revoked" ? "border-sm-ink/8 bg-sm-ink/[0.02] opacity-70" : "border-sm-ink/10 bg-white"}`} data-testid={`team-member-${member.email}`}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate font-display text-base font-bold text-sm-ink">{member.email}</div>
            <AccessBadge state={member.state} />
            {member.policyManaged ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700" title={AUTOMATIC_ACCESS_DESCRIPTION}>
                <LockKeyhole size={12} /> {AUTOMATIC_ACCESS_LABEL}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-sm-ink/50">
            {member.state === "pending"
              ? "Waiting for this exact company identity to sign in."
              : member.state === "active"
                ? `Identity verified. Updated ${formatTime(member.updatedAt)}.`
                : `Access removed ${formatTime(member.updatedAt)}.`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {editable ? (
            <select
              aria-label={`Role for ${member.email}`}
              value={member.role}
              onChange={(event) => onRoleChange(member, event.target.value)}
              className="rounded-xl border border-sm-ink/15 bg-white px-3 py-2 text-sm font-bold text-sm-ink outline-none focus:border-sm-primary/50 focus:ring-4 focus:ring-sm-primary/10"
            >
              {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.name}</option>)}
            </select>
          ) : (
            <span className="rounded-xl border border-sm-ink/10 bg-sm-ink/[0.035] px-3 py-2 text-sm font-bold text-sm-ink/65">
              {ROLE_LABELS[member.role] ?? member.role}
            </span>
          )}

          {removable && !confirming ? (
            <button
              type="button"
              onClick={() => onRequestRemove(member.email)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-50"
            >
              <Trash2 size={14} /> Remove access
            </button>
          ) : null}
          {confirming ? (
            <>
              <button
                type="button"
                onClick={() => onConfirmRemove(member)}
                disabled={busy}
                className="rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-45"
              >
                Confirm remove
              </button>
              <button
                type="button"
                onClick={onCancelRemove}
                disabled={busy}
                className="rounded-xl border border-sm-ink/15 bg-white px-3 py-2 text-sm font-bold text-sm-ink/65 disabled:opacity-45"
              >
                Keep access
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const { activeClient, api, readOnly = false, readOnlyMessage } = useApp();
  const resource = useLiveResource("getTeam");
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState(null);

  if (resource.status === "loading" && !resource.data) {
    return <PageLoading label="Loading Team access…" />;
  }
  if (resource.status === "error") {
    return <PageError error={resource.error} onRetry={resource.reload} title="Team access unavailable" />;
  }

  const data = resource.data ?? {};
  const members = Array.isArray(data.members) ? data.members : [];
  const operatingRoster = Array.isArray(data.operatingRoster) ? data.operatingRoster : [];
  const access = data.accessManagement ?? {
    status: "unavailable",
    canManage: false,
    actorRole: null,
    allowedRoles: [],
  };
  // Under View as the page shows what the person could do here, and does none of it.
  const canManage = access.status === "available" && access.canManage === true && !readOnly;

  const orderedMembers = [...members].sort((left, right) => {
    const stateRank = { active: 0, pending: 1, revoked: 2 };
    return (stateRank[left.state] ?? 9) - (stateRank[right.state] ?? 9) || left.email.localeCompare(right.email);
  });

  const runAction = async (action, successMessage) => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await action();
      await resource.reload();
      setNotice(successMessage);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addMember = async ({ email, role }) => {
    const completed = await runAction(
      () => api.addTeamMember({ email, role }),
      `${email} now has pending access to ${activeClient?.clientName ?? "this workspace"}.`
    );
    if (completed) setShowAdd(false);
  };

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    await runAction(
      () => api.setTeamMemberRole({ email: member.email, role }),
      `${member.email} is now ${ROLE_LABELS[role] ?? role}.`
    );
  };

  const removeMember = async (member) => {
    const completed = await runAction(
      () => api.revokeTeamMember({ email: member.email }),
      `${member.email} no longer has access to this workspace.`
    );
    if (completed) setConfirmEmail(null);
  };

  return (
    <div className="fade-up">
      <PageHeader
        testId="team-header"
        title="Team & Access"
        subtitle={`Automatic company-domain access, exact-email access, and workspace roles for ${activeClient?.clientName ?? "this workspace"}.`}
      >
        {readOnly ? <p data-testid="team-read-only" role="status" className="text-sm font-semibold text-[#7C3A00]">{readOnlyMessage}</p> : null}
        {canManage ? (
          <button
            data-testid="add-member-btn"
            type="button"
            onClick={() => {
              setShowAdd((value) => !value);
              setActionError(null);
              setNotice(null);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-sm-primary px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.09em] text-white transition hover:bg-sm-primary/90"
          >
            <UserPlus size={14} /> Add member
          </button>
        ) : null}
      </PageHeader>

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        {ROLE_DEFINITIONS.map((role) => {
          const yours = role.value === access.actorRole;
          return (
            <div
              key={role.value}
              data-testid={`team-role-${role.value}`}
              className={`rounded-2xl border bg-white p-4 ${yours ? "border-sm-accent/45 shadow-[0_0_0_1px_rgba(70,144,217,0.25)]" : "border-sm-ink/10"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-display text-sm font-bold text-sm-ink">
                  <ShieldCheck size={15} className="text-sm-primary" /> {role.name}
                </div>
                {yours ? <span data-testid="team-role-yours" className="rounded-full bg-sm-accent/12 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.1em] text-sm-primary">You</span> : null}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-sm-ink/55">{role.description}</p>
              <p className={`mt-1.5 text-xs leading-relaxed ${yours ? "font-bold text-sm-primary" : "text-sm-ink/45"}`}>{role.onThisPage}</p>
            </div>
          );
        })}
      </div>

      {showAdd && canManage ? (
        <AddMemberPanel access={access} onCancel={() => setShowAdd(false)} onSubmit={addMember} busy={busy} />
      ) : null}

      {notice ? (
        <div role="status" className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">{notice}</div>
      ) : null}
      {actionError ? (
        <div role="alert" className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <div className="font-bold">Team update failed</div>
          <div className="mt-1">{actionError.message || "The workspace did not accept this change."}</div>
        </div>
      ) : null}

      <section className="rounded-3xl border border-sm-ink/10 bg-white/70 p-5 md:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold text-sm-ink">Workspace members</h2>
              {/* The rule that puts people on this list belongs next to the list, where a row can carry its badge. */}
              <HelpTip label="How people get on this list" className="text-left">
                Anyone who signs in with Google using this workspace's saved company email domain joins automatically as a
                Member, and XScale staff can reach every client workspace as Owners. A row marked{" "}
                <strong>{AUTOMATIC_ACCESS_LABEL}</strong> comes from one of those rules, so its baseline access cannot be
                removed here. Everyone else is added by email, and their access starts only when that exact address signs in.
              </HelpTip>
            </div>
            <p className="mt-1 text-sm text-sm-ink/55" data-testid="team-members-note">
              Signing in with an approved company email joins automatically. Added emails activate once that exact address signs in.
            </p>
          </div>
          <div className="rounded-full border border-sm-ink/10 bg-white px-3 py-1.5 text-xs font-bold text-sm-ink/55">
            {members.filter((member) => member.state !== "revoked").length} with access
          </div>
        </div>

        {orderedMembers.length === 0 ? (
          <EmptyState icon={UsersRound} title="No workspace members reported" description="Add an exact email to create pending access for this brand workspace." />
        ) : (
          <div className="space-y-3">
            {orderedMembers.map((member) => (
              <MemberRow
                key={`${member.id}:${member.email}`}
                member={member}
                access={access}
                busy={busy}
                readOnly={readOnly}
                confirmEmail={confirmEmail}
                onRoleChange={changeRole}
                onRequestRemove={setConfirmEmail}
                onConfirmRemove={removeMember}
                onCancelRemove={() => setConfirmEmail(null)}
              />
            ))}
          </div>
        )}
      </section>

      {operatingRoster.length > 0 ? (
        <section className="mt-6 rounded-3xl border border-sm-ink/10 bg-white/70 p-5 md:p-6">
          <h2 className="font-display text-xl font-bold text-sm-ink">Operating roster</h2>
          <p className="mt-1 text-sm text-sm-ink/55">Role owners and communication rhythms configured for this brand.</p>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {operatingRoster.map((person) => (
              <div key={person.id} className="rounded-2xl border border-sm-ink/10 bg-white p-4">
                <div className="font-display text-sm font-bold text-sm-ink">{person.name}</div>
                <div className="mt-1 text-xs font-semibold text-sm-primary">{person.role || "Role not reported"}</div>
                {person.whatTheyDo ? <p className="mt-2 text-xs leading-relaxed text-sm-ink/55">{person.whatTheyDo}</p> : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
